import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, Link } from 'react-router-dom';
import { BiPencil } from 'react-icons/bi';

import { API_BASE_URL } from '../utils/urls.js';

const MyTasks = () => {
    const { userId } = useParams();
    const [tasks, setTasks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchTasks = async () => {
            const token = localStorage.getItem('token');
            if (!token) {
                setError('Authentication token not found.');
                setLoading(false);
                return;
            }
            try {
                const response = await axios.get(`${API_BASE_URL}/tasks/user/${userId}`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                setTasks(response.data);
            } catch (err) {
                console.error('Error fetching tasks:', err);
                setError(err.response?.data?.message || 'Failed to load tasks. Please try again.');
            } finally {
                setLoading(false);
            }
        };

        if (userId) {
            fetchTasks();
        } else {
            setError('No user ID provided.');
            setLoading(false);
        }
    }, [userId]);

    if (loading) return <div className="p-6 text-center">Loading tasks...</div>;
    if (error) return <div className="p-6 text-center text-red-500">{error}</div>;

    return (
        <div className="p-6">
            <h1 className="text-3xl font-bold text-gray-800 mb-6">My Assigned Tasks</h1>
            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Case</th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Due Date</th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                            <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {tasks.length > 0 ? (
                            tasks.map(task => (
                                <tr key={task.id} className="hover:bg-gray-50 transition-colors duration-200">
                                    {/* Task Title */}
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{task.task_title}</td>
                                    
                                    {/* Case Link (Minimal Detail) */}
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        <Link to={`/cases/${task.case_id}`} className="text-blue-600 hover:underline">
                                            {task.case_name || `Case ID: ${task.case_id}`}
                                        </Link>
                                    </td>
                                    
                                    {/* Due Date (Minimal Detail) */}
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {new Date(task.end_time).toLocaleDateString()}
                                    </td>
                                    
                                    {/* Status (Minimal Detail) */}
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span
                                            className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                                task.status === "completed"
                                                    ? "bg-green-100 text-green-800"
                                                    : "bg-yellow-100 text-yellow-800"
                                            }`}
                                        >
                                            {task.status || "Pending"}
                                        </span>
                                    </td>
                                    
                                    {/* Actions (Minimal Detail) */}
                                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                        <Link
                                            to={`/agent/tasks/edit/${task.id}`}
                                            className="text-blue-600 hover:text-blue-900 inline-flex items-center"
                                            title="Update Status"
                                        >
                                            <BiPencil className="inline-block h-5 w-5" />
                                        </Link>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="5" className="px-6 py-4 text-center text-sm text-gray-500">No tasks found.</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default MyTasks;